import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled(motion.h1)`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.colors.secondary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100px;
    height: 4px;
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  }
`;

interface ProjectImageProps {
  imageUrl: string;
}

const ProjectImage = styled.div<ProjectImageProps>`
  height: 200px;
  background-color: rgba(252, 232, 232, 0.12);
  background-image: url(${props => props.imageUrl});
  background-size: cover;
  background-position: center;
`;

const ProjectInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  height: 100%;
`;

const ProyectTopInfo = styled.div`
  padding: 1.5rem;
  padding-bottom: 0;
`;

const ProyectBottomInfo = styled.div`
  padding: 1.5rem;
  margin-top: auto;
`;

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.8rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const ProjectDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TechTag = styled.span`
  display: inline-block;
  background-color: rgba(252, 232, 232, 0.12);
  color: ${({ theme }) => theme.colors.text};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.9rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

const ProjectLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`;

interface ProjectLinkProps {
  primary?: boolean;
}

const ProjectLink = styled.button<ProjectLinkProps>`
  display: inline-block;
  padding: 0.6rem 1.2rem;
  background-color: ${({ primary, theme }) => primary ? theme.colors.primary : 'transparent'};
  color: ${({ primary, theme }) => primary ? theme.colors.text : theme.colors.secondary};
  border: 2px solid ${({ primary, theme }) => primary ? theme.colors.primary : theme.colors.secondary};
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s;
  cursor: pointer;
  
  &:hover {
    background-color: ${({ primary, theme }) => primary ? theme.colors.primary : theme.colors.secondary};
    color: ${({ theme }) => theme.colors.text};
  }
`;

// Modal Overlay (fondo oscuro)
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

// Contenedor del Modal
const ModalContainer = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: 10px;
  padding: 1.5rem;
  min-width: 300px;
  max-width: 80%;
  width: auto;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  position: relative;
`;

// Título del Modal
const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-top: 0;
  margin-bottom: 1rem;
  padding-right: 2rem;
`;

// Contenido del Modal - Imagen
const ModalImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 5px;
  margin-bottom: 1rem;
`;

// Descripción completa en el Modal
const ModalDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1.5rem;
`;

// Botón para cerrar el Modal
const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.textLight};
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// Componente Modal
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  imageWeb: string;
  imageMobile: string;
  description: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, imageWeb, imageMobile, description }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Efecto para detectar cambios en el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Manejar clic fuera del modal para cerrarlo
  const handleClickOutside = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  // Manejar la tecla ESC para cerrar el modal
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevenir scroll en el cuerpo cuando el modal está abierto
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      // Restaurar scroll cuando el modal se cierra
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  // Variante de animación para el overlay
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  // Variante de animación para el modal
  const modalVariants = {
    hidden: { opacity: 0, y: -50, scale: 0.9 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { type: 'spring', damping: 25, stiffness: 300 }
    },
    exit: { 
      opacity: 0, 
      y: 50, 
      scale: 0.9,
      transition: { duration: 0.2 }
    }
  };

  // Mostrar la imagen adecuada según el dispositivo
  const currentImage = isMobile ? imageMobile : imageWeb;

  // Usar un portal para renderizar el modal directamente en el body
  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={overlayVariants}
          onClick={handleClickOutside}
        >
          <ModalContainer
            ref={modalRef}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={onClose}>&times;</CloseButton>
            <ModalTitle>{title}</ModalTitle>
            <ModalImage src={currentImage} alt={title} />
            <ModalDescription>{description}</ModalDescription>
          </ModalContainer>
        </ModalOverlay>
      )}
    </AnimatePresence>,
    document.body // Renderizar en el body, no en el componente
  );
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

const PortfolioPage: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const [activeProject, setActiveProject] = useState<{ 
    title: string;
    image_web: string;
    image_mobile: string;
    description: string;
  } | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      import("../utils/secureStorage").then(({ getSecureStorage }) => {
        const secureData = getSecureStorage();
        const token = secureData.token;
        if (token) {
          setIsLoggedIn(true);
        }
      });
    } catch (e) {}
  }, []);

  const { t } = useTranslation();
  
  const projects = t('portfolio.projects', { returnObjects: true }) as any[];
  const projectsWithoutNoLogin = projects.map((item: any) => {
    return {
      ...item,
      title: "XXXX XXXX XXXX",
      description: "XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX",
      image: "favicon.ico",
    };
  });

  // Función para manejar el clic en "Ver Proyecto"
  const handleProjectClick = (project: { 
    title: string;
    image_web: string;
    image_mobile: string;
    description: string;
  }) => {
    setActiveProject(project);
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setActiveProject(null);
  };

  return (
    <PageContainer as={motion.div} variants={containerVariants} initial="hidden" animate="visible">
      <PageTitle variants={itemVariants}>{t('portfolio.title')}</PageTitle>
      
      <ProjectsGrid>
        { isLoggedIn ? projects.map((project, index) => (
          <ProjectCard key={index} variants={itemVariants}>
            <ProjectImage imageUrl={project.image} />
            <ProjectInfo>
              <ProyectTopInfo>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
              </ProyectTopInfo>
              <ProyectBottomInfo>
              <div>
                {project.technologies.map((tech: string, idx: number) => (
                  <TechTag key={idx}>{tech}</TechTag>
                ))}
              </div>
              <ProjectLinks>
                <ProjectLink 
                  onClick={() => handleProjectClick({ 
                    title: project.title, 
                    image_web: project.image_web, 
                    image_mobile: project.image_mobile,
                    description: project.description 
                  })}
                  primary={true}
                >
                  {t('portfolio.viewProject')}
                </ProjectLink>
              </ProjectLinks>
              </ProyectBottomInfo>
            </ProjectInfo>
          </ProjectCard>
        )) : projectsWithoutNoLogin.map((item: any, index: number) => (
          <ProjectCard key={index} variants={itemVariants} className="blur-effect" onClick={() => navigate("/login")}>
            <ProjectImage imageUrl={item.image} />
            <ProjectInfo>
              <ProyectTopInfo>
                <ProjectTitle>{item.title}</ProjectTitle>
                <ProjectDescription>{item.description}</ProjectDescription>
              </ProyectTopInfo>
            </ProjectInfo>
          </ProjectCard>
        ))}
      </ProjectsGrid>

      {/* Modal para mostrar la imagen del proyecto */}
      <Modal
        isOpen={activeProject !== null}
        onClose={handleCloseModal}
        title={activeProject?.title || ''}
        imageWeb={activeProject?.image_web || ''}
        imageMobile={activeProject?.image_mobile || ''}
        description={activeProject?.description || ''}
      />
    </PageContainer>
  );
};

export default PortfolioPage; 